import { Routes } from '@angular/router';
import { authGuard } from '../shared/guards/auth.guard';
import { publicGuard } from '../shared/guards/public.guard';
import { adminGuard } from '../shared/guards/admin.guard';

export const routes: Routes = [
  // IMPORTANTE - orden de este arreglo: 'login', 'register',
  // 'forgot-password', 'profile' y 'admin' van ANTES que el layout principal
  // ('', más abajo). El router de Angular prueba las rutas de nivel raíz en
  // el ORDEN en que aparecen acá, y el layout principal ahora tiene una ruta
  // hija comodín ('**', para la página 404 - ver más abajo) que matchea
  // CUALQUIER URL que no sea alguna de sus otras rutas hijas. Si el layout
  // principal quedara primero en este arreglo, esa ruta comodín interceptaría
  // /login, /register, /admin, etc. ANTES de que el router llegara a
  // probarlas como rutas raíz separadas (bug real: entrar a /login mostraba
  // la página 404 en vez del formulario de login). Poniendo estas rutas
  // específicas primero, el router las encuentra y usa esas ANTES de
  // siquiera intentar el layout principal.
  {
    path: 'login',
    loadComponent: () => import('./layouts/main-layout/main-layout.component').then(m => m.MainLayoutComponent),
    title: 'Login - Hakire',
    canActivate: [publicGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('./authentication/login/login.component').then(m => m.LoginComponent),
        title: 'Login - Hakire'
      }
    ]
  },
  {
    path: 'auth',
    redirectTo: 'login',
    pathMatch: 'full'
  },
  {
    path: 'register',
    loadComponent: () => import('./layouts/main-layout/main-layout.component').then(m => m.MainLayoutComponent),
    title: 'Registro - Hakire',
    canActivate: [publicGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('./authentication/register/register.component').then(m => m.RegisterComponent),
        title: 'Registro - Hakire'
      }
    ]
  },
  {
    path: 'forgot-password',
    loadComponent: () => import('./layouts/main-layout/main-layout.component').then(m => m.MainLayoutComponent),
    title: 'Recuperar Contraseña - Hakire',
    canActivate: [publicGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('./authentication/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent),
        title: 'Recuperar Contraseña - Hakire'
      }
    ]
  },
  {
    // Antes había un ProfileLayoutComponent envolviendo a ProfileComponent en
    // una ruta intermedia con path: '' que no aportaba nada (solo un div de
    // fondo repetido) y duplicaba la jerarquía de rutas. ProfileComponent ya
    // es el layout completo (sidebar + router-outlet), así que se usa
    // directamente como componente de la ruta 'profile'.
    path: 'profile',
    loadComponent: () => import('./profile/profile.component').then(m => m.ProfileComponent),
    title: 'Mi Perfil - Hakire',
    canActivate: [authGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('./profile/profile-dashboard/profile-dashboard.component').then(m => m.ProfileDashboardComponent),
        title: 'Dashboard - Hakire'
      },
      {
        // Antes había 3 rutas separadas (orders, orders/active, orders/history)
        // apuntando al mismo componente. Al ser rutas distintas, cada clic en
        // el sidebar destruía y recreaba el componente, volviendo a pedir
        // todos los pedidos al backend antes de aplicar el filtro. Ahora es
        // una sola ruta y el filtro viaja como query param (?filter=active),
        // que ProfileOrdersComponent lee de forma reactiva sin recargar.
        path: 'orders',
        loadComponent: () => import('./profile/profile-orders/profile-orders.component').then(m => m.ProfileOrdersComponent),
        title: 'Mis Pedidos - Hakire'
      },
      {
        path: 'templates',
        loadComponent: () => import('./profile/profile-templates/profile-templates.component').then(m => m.ProfileTemplatesComponent),
        title: 'Mis Plantillas - Hakire'
      },
      {
        path: 'wishlist',
        loadComponent: () => import('./profile/profile-wishlist/profile-wishlist.component').then(m => m.ProfileWishlistComponent),
        title: 'Mis Favoritos - Hakire'
      },
      {
        path: 'sessions',
        loadComponent: () => import('./profile/profile-sessions/profile-sessions.component').then(m => m.ProfileSessionsComponent),
        title: 'Mis Sesiones - Hakire'
      },
      {
        path: 'api-token',
        loadComponent: () => import('./profile/profile-api-token/profile-api-token.component').then(m => m.ProfileApiTokenComponent),
        title: 'Token de API - Hakire'
      },
      {
        path: 'designs',
        loadComponent: () => import('./profile/profile-designs/profile-designs.component').then(m => m.ProfileDesignsComponent),
        title: 'Mis Diseños - Hakire'
      },
      {
        path: 'pqrs',
        loadComponent: () => import('./profile/profile-pqrs/profile-pqrs-list.component').then(m => m.ProfilePqrsListComponent),
        title: 'PQRS - Hakire'
      },
      {
        path: 'pqrs/:id',
        loadComponent: () => import('./profile/profile-pqrs/profile-pqrs-detail.component').then(m => m.ProfilePqrsDetailComponent),
        title: 'PQRS - Hakire'
      },
      {
        path: 'info',
        loadComponent: () => import('./profile/profile-info/profile-info.component').then(m => m.ProfileInfoComponent),
        title: 'Mi Información - Hakire'
      },
      {
        path: 'addresses',
        loadComponent: () => import('./profile/profile-addresses/profile-addresses.component').then(m => m.ProfileAddressesComponent),
        title: 'Mis Direcciones - Hakire'
      },
      {
        path: 'settings',
        loadComponent: () => import('./profile/profile-settings/profile-settings.component').then(m => m.ProfileSettingsComponent),
        title: 'Configuración - Hakire'
      }
    ]
  },
  {
    path: 'admin',
    // Layout propio del panel admin (sidebar con su menú, sin header/carrito/
    // footer de la tienda) — antes reutilizaba MainLayoutComponent, que es el
    // shell de la tienda pública y no tenía sentido aquí.
    loadComponent: () => import('./layouts/admin-layout/admin-layout.component').then(m => m.AdminLayoutComponent),
    title: 'Admin - Hakire',
    canActivate: [authGuard, adminGuard],
    // También revalida en cada navegación DENTRO de /admin (ej. pasar del
    // listado de carritos abandonados al detalle), no solo al entrar la
    // primera vez — así una sesión que deja de ser admin a mitad de
    // navegación no puede seguir moviéndose por rutas hijas.
    canActivateChild: [authGuard, adminGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('./admin/admin-dashboard/admin-dashboard.component').then(m => m.AdminDashboardComponent),
        title: 'Panel admin - Hakire'
      },
      {
        path: 'products',
        loadComponent: () => import('./admin/products/products-list.component').then(m => m.ProductsListComponent),
        title: 'Productos - Hakire'
      },
      {
        path: 'categories',
        loadComponent: () => import('./admin/categories/categories-list.component').then(m => m.CategoriesListComponent),
        title: 'Categorías - Hakire'
      },
      {
        path: 'collections',
        loadComponent: () => import('./admin/product-collections/product-collections.component').then(m => m.ProductCollectionsComponent),
        title: 'Colecciones - Hakire'
      },
      {
        path: 'notes',
        loadComponent: () => import('./admin/notes/notes-list.component').then(m => m.NotesListComponent),
        title: 'Bloc de notas - Hakire'
      },
      {
        path: 'product-types',
        loadComponent: () => import('./admin/product-types/product-types-list.component').then(m => m.ProductTypesListComponent),
        title: 'Tipos de producto - Hakire'
      },
      {
        path: 'colors-designs',
        loadComponent: () => import('./admin/colors-designs/colors-designs-list.component').then(m => m.ColorsDesignsListComponent),
        title: 'Colores y diseños - Hakire'
      },
      {
        path: 'coupons',
        loadComponent: () => import('./admin/coupons/coupons-list.component').then(m => m.CouponsListComponent),
        title: 'Cupones - Hakire'
      },
      {
        path: 'shipping-setup',
        loadComponent: () => import('./admin/shipping/shipping-list.component').then(m => m.ShippingListComponent),
        title: 'Envíos - Hakire'
      },
      {
        path: 'payment-gateways',
        loadComponent: () => import('./admin/payment-gateways/payment-gateways-list.component').then(m => m.PaymentGatewaysListComponent),
        title: 'Pasarelas de pago - Hakire'
      },
      {
        path: 'stock-alerts',
        loadComponent: () => import('./admin/stock-alerts/stock-alerts-list.component').then(m => m.StockAlertsListComponent),
        title: 'Stock bajo - Hakire'
      },
      {
        path: 'returns',
        loadComponent: () => import('./admin/returns/returns-list.component').then(m => m.ReturnsListComponent),
        title: 'Devoluciones - Hakire'
      },
      {
        path: 'newsletter',
        loadComponent: () => import('./admin/newsletter/newsletter-list.component').then(m => m.NewsletterListComponent),
        title: 'Newsletter - Hakire'
      },
      {
        path: 'blog',
        loadComponent: () => import('./admin/blog/blog-posts-list.component').then(m => m.BlogPostsListComponent),
        title: 'Blog - Hakire'
      },
      {
        path: 'brand-settings',
        loadComponent: () => import('./admin/brand-settings/brand-settings.component').then(m => m.BrandSettingsComponent),
        title: 'Marca - Hakire'
      },
      {
        path: 'products/new',
        loadComponent: () => import('./admin/products/product-form.component').then(m => m.ProductFormComponent),
        title: 'Crear producto - Hakire'
      },
      {
        path: 'products/:id/edit',
        loadComponent: () => import('./admin/products/product-form.component').then(m => m.ProductFormComponent),
        title: 'Editar producto - Hakire'
      },
      {
        path: 'promotions',
        loadComponent: () => import('./admin/promotions/promotions.component').then(m => m.PromotionsComponent),
        title: 'Promociones - Hakire'
      },
      {
        path: 'profit-margin',
        loadComponent: () => import('./admin/profit-margin/profit-margin.component').then(m => m.ProfitMarginComponent),
        title: 'Costos de Productos - Hakire'
      },
      {
        path: 'orders',
        loadComponent: () => import('./admin/orders/orders-list.component').then(m => m.OrdersListComponent),
        title: 'Pedidos - Hakire'
      },
      {
        path: 'orders/:id',
        loadComponent: () => import('./admin/orders/order-detail.component').then(m => m.OrderDetailComponent),
        title: 'Detalle de pedido - Hakire'
      },
      {
        path: 'custom-designs',
        loadComponent: () => import('./admin/custom-designs/custom-designs-list.component').then(m => m.CustomDesignsListComponent),
        title: 'Diseños personalizados - Hakire'
      },
      {
        path: 'mockups',
        loadComponent: () => import('./admin/mockup-generator/mockup-generator.component').then(m => m.MockupGeneratorComponent),
        title: 'Generador de miniaturas - Hakire'
      },
      {
        path: 'users',
        loadComponent: () => import('./admin/users/users-list.component').then(m => m.UsersListComponent),
        title: 'Usuarios - Hakire'
      },
      {
        path: 'stats',
        loadComponent: () => import('./admin/stats/stats.component').then(m => m.StatsComponent),
        title: 'Estadísticas - Hakire'
      },
      {
        path: 'packaging',
        loadComponent: () => import('./admin/packaging/packaging-list.component').then(m => m.PackagingListComponent),
        title: 'Empaques y tallas - Hakire'
      },
      {
        path: 'banners',
        loadComponent: () => import('./admin/banners/banners-list.component').then(m => m.BannersListComponent),
        title: 'Banners - Hakire'
      },
      {
        path: 'short-links',
        loadComponent: () => import('./admin/short-links/short-links-list.component').then(m => m.ShortLinksListComponent),
        title: 'Links cortos - Hakire'
      },
      {
        path: 'parameters',
        loadComponent: () => import('./admin/parameters/parameters-list.component').then(m => m.ParametersListComponent),
        title: 'Configuración - Hakire'
      },
      {
        path: 'email-templates',
        loadComponent: () => import('./admin/email-templates/email-templates-list.component').then(m => m.EmailTemplatesListComponent),
        title: 'Plantillas de correo - Hakire'
      },
      {
        path: 'email-logs',
        loadComponent: () => import('./admin/email-logs/email-logs-list.component').then(m => m.EmailLogsListComponent),
        title: 'Email Logs - Hakire'
      },
      {
        path: 'audit-logs',
        loadComponent: () => import('./admin/audit-logs/audit-logs-list.component').then(m => m.AuditLogsListComponent),
        title: 'Auditoría - Hakire'
      },
      {
        path: 'transaction-ledger',
        loadComponent: () => import('./admin/transaction-ledger/transaction-ledger-list.component').then(m => m.TransactionLedgerListComponent),
        title: 'Contabilidad - Hakire'
      },
      {
        path: 'role-permissions',
        loadComponent: () => import('./admin/role-permissions/role-permissions.component').then(m => m.RolePermissionsComponent),
        title: 'Roles y permisos - Hakire'
      },
      {
        path: 'reviews',
        loadComponent: () => import('./admin/reviews/reviews-list.component').then(m => m.ReviewsListComponent),
        title: 'Reseñas - Hakire'
      },
      {
        path: 'account-deletions',
        loadComponent: () => import('./admin/account-deletions/account-deletions-list.component').then(m => m.AccountDeletionsListComponent),
        title: 'Eliminación de cuentas - Hakire'
      },
      {
        path: 'pqrs',
        loadComponent: () => import('./admin/pqrs/pqrs-list.component').then(m => m.PqrsListComponent),
        title: 'PQRS - Hakire'
      },
      {
        path: 'pqrs/:id',
        loadComponent: () => import('./admin/pqrs/pqrs-detail.component').then(m => m.PqrsDetailComponent),
        title: 'PQRS - Hakire'
      },
      {
        path: 'pqrs-categories',
        loadComponent: () => import('./admin/pqrs-categories/pqrs-categories-list.component').then(m => m.PqrsCategoriesListComponent),
        title: 'Categorías PQRS - Hakire'
      },
      {
        path: 'abandoned-carts',
        loadComponent: () => import('./admin/abandoned-carts/abandoned-carts-list.component').then(m => m.AbandonedCartsListComponent),
        title: 'Carritos abandonados - Hakire'
      },
      {
        path: 'abandoned-carts/:cartId',
        loadComponent: () => import('./admin/abandoned-carts/abandoned-cart-detail.component').then(m => m.AbandonedCartDetailComponent),
        title: 'Carrito abandonado - Hakire'
      }
    ]
  },
  {
    path: '',
    loadComponent: () => import('./layouts/main-layout/main-layout.component').then(m => m.MainLayoutComponent),
    title: 'Hakire - Colección Exclusiva',
    children: [
      {
        path: '',
        loadComponent: () => import('./home/home.component').then(m => m.HomeComponent),
        title: 'Hakire - Colección Exclusiva'
      },
      {
        path: 'products/:slug',
        loadComponent: () => import('./products/product-detail/product-detail.component').then(m => m.ProductDetailComponent),
        title: 'Detalle del Producto - Hakire'
      },
      {
        path: 'checkout/cart',
        loadComponent: () => import('./cart/cart.component').then(m => m.CartComponent),
        title: 'Checkout - Hakire'
      },
      {
        path: 'checkout/payment/success',
        loadComponent: () => import('./checkout/payment-result/payment-result.component').then(m => m.PaymentResultComponent),
        title: 'Pago aprobado - Hakire',
        data: { paymentStatus: 'success' }
      },
      {
        path: 'checkout/payment/failure',
        loadComponent: () => import('./checkout/payment-result/payment-result.component').then(m => m.PaymentResultComponent),
        title: 'Pago rechazado - Hakire',
        data: { paymentStatus: 'failure' }
      },
      {
        path: 'checkout/payment/pending',
        loadComponent: () => import('./checkout/payment-result/payment-result.component').then(m => m.PaymentResultComponent),
        title: 'Pago pendiente - Hakire',
        data: { paymentStatus: 'pending' }
      },
      {
        path: 'categorias/:category',
        loadComponent: () => import('./categories/category-page.component').then(m => m.CategoryPageComponent),
        title: 'Categoría - Hakire'
      },
      {
        path: 'colecciones/:id',
        loadComponent: () => import('./collections/collection-page.component').then(m => m.CollectionPageComponent),
        title: 'Colección - Hakire'
      },
      {
        path: 'catalogo',
        loadComponent: () => import('./catalog/catalog-page.component').then(m => m.CatalogPageComponent),
        title: 'Catálogo - Hakire'
      },
      {
        path: 'new',
        loadComponent: () => import('./new/new.component').then(m => m.NewComponent),
        title: 'Novedades - Hakire'
      },
      {
        path: 'personalizado',
        loadComponent: () => import('./personalizado/personalizado.component').then(m => m.PersonalizadoComponent),
        title: 'Personalizado - Hakire'
      },
      {
        path: 'cups/design-your-cup',
        loadComponent: () => import('./cups/design-your-cup/design-your-cup.component').then(m => m.DesignYourCupComponent),
        title: 'Diseña tu taza - Hakire',
        // Requiere login: el diseño se guarda contra /me/designs, atado al
        // usuario (ver me-designs.routes.js en el backend).
        canActivate: [authGuard]
      },
      {
        // Retomar un diseño guardado desde "Mis Diseños" (perfil) - mismo
        // componente, solo que carga el diseño existente por id en vez de
        // arrancar en blanco (ver maybeLoadExistingDesign).
        path: 'cups/design-your-cup/:id',
        loadComponent: () => import('./cups/design-your-cup/design-your-cup.component').then(m => m.DesignYourCupComponent),
        title: 'Diseña tu taza - Hakire',
        canActivate: [authGuard]
      },
      {
        path: 'camisetas/design-your-shirt',
        loadComponent: () => import('./camisetas/design-your-shirt/design-your-shirt.component').then(m => m.DesignYourShirtComponent),
        title: 'Diseña tu camiseta - Hakire',
        // Requiere login: el diseño se guarda contra /me/designs, atado al
        // usuario (ver me-designs.routes.js en el backend).
        canActivate: [authGuard]
      },
      {
        path: 'camisetas/design-your-shirt/:id',
        loadComponent: () => import('./camisetas/design-your-shirt/design-your-shirt.component').then(m => m.DesignYourShirtComponent),
        title: 'Diseña tu camiseta - Hakire',
        canActivate: [authGuard]
      },
      {
        path: 'search',
        loadComponent: () => import('./search/search.component').then(m => m.SearchComponent),
        title: 'Buscar - Hakire'
      },
      {
        // Pública a propósito (sin publicGuard ni authGuard): Meta la exige
        // para verificar el número de WhatsApp Business, y cualquier
        // visitante -con o sin cuenta- debe poder leerla.
        path: 'politica-de-privacidad',
        loadComponent: () => import('./legal/privacy-policy/privacy-policy.component').then(m => m.PrivacyPolicyComponent),
        title: 'Política de Privacidad - Hakire'
      },
      {
        // Cualquier URL bajo el sitio público que no calce con ninguna de las
        // rutas de arriba (antes esto caía en el wildcard de más abajo, que
        // redirigía a /login en silencio, sin avisar que la URL no existe).
        // Va al final de este arreglo a propósito: el router prueba las
        // rutas hijas en orden, así que solo llega acá si ninguna otra ya
        // matcheó. Queda DENTRO del layout principal para que el usuario siga
        // viendo el header/footer y pueda navegar de vuelta normalmente.
        //
        // IMPORTANTE: por esta misma razón (matchea CUALQUIER URL), esta
        // ruta padre ('', el layout principal) tiene que ir DESPUÉS de
        // 'login'/'register'/'forgot-password'/'profile'/'admin' en el
        // arreglo de arriba - si fuera la primera, este comodín interceptaría
        // /login, /admin, etc. antes de que el router llegara a probarlas.
        path: '**',
        loadComponent: () => import('./not-found/not-found.component').then(m => m.NotFoundComponent),
        title: 'Página no encontrada - Hakire'
      }
    ]
  },
  {
    path: '**',
    redirectTo: '/login',
    pathMatch: 'full'
  }
];
